import { useEffect, useState } from 'react';
import axios from 'axios';
import LightAdmin from 'components/headers/lightAdmin';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import Footer from "components/footers/Footer.jsx";
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const ProductSalesAdmin = () => {
    const { t } = useTranslation();
    const [stats, setStats] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState('Tout le temps'); // default all time
    const navigate = useNavigate();

    useEffect(() => {
        const role = localStorage.getItem('role'); 
        const adminType = localStorage.getItem('admin_type'); 
        setIsSuperAdmin(role === 'admin' && adminType === 'super');
    }, []);

    useEffect(() => {
        if (isSuperAdmin) fetchStats();
    }, [isSuperAdmin, selectedMonth]);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const productsResp = await axios.get('https://rehabwmg.online/api/admin/product', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const products = productsResp.data;

            const ordersResp = await axios.get('https://rehabwmg.online/api/admin/orders', {
                headers: { Authorization: `Bearer ${token}` }
            });
            let allOrders = ordersResp.data.data || ordersResp.data;
            allOrders = allOrders.filter(order => order.Status === "livré");

            // Filter orders by selected month dynamically
            if (selectedMonth !== 'Tout le temps') {
                const [monthName, year] = selectedMonth.split(' ');
                const monthIndex = [
                    'janvier','février','mars','avril','mai','juin',
                    'juillet','août','septembre','octobre','novembre','décembre'
                ].indexOf(monthName);

                allOrders = allOrders.filter(order => {
                    const orderDate = new Date(order.created_at);
                    return orderDate.getFullYear() === parseInt(year) && orderDate.getMonth() === monthIndex;
                });
            }

            // Compute stats per product
            const computedStats = products.map(product => {
                let productOrders = [];
                allOrders.forEach(order => {
                    if (order.order_items && order.order_items.length) {
                        const matchedItems = order.order_items.filter(item => item.ProductID === product.ProductID);
                        if (matchedItems.length) productOrders.push(...matchedItems);
                    }
                });

                const totalSold = productOrders.reduce((sum, item) => sum + item.Quantity, 0);
                const totalRevenue = productOrders.reduce((sum, item) => sum + (item.Price * item.Quantity), 0);
                const adminPercentage = totalRevenue * 0.3;

                return {
                    ProductID: product.ProductID,
                    Ref: product.Ref,
                    Name: product.Name?.fr || '',
                    TotalSold: totalSold,
                    TotalRevenue: totalRevenue,
                    AdminPercentage: adminPercentage
                };
            });

            setStats(computedStats);
        } catch (error) {
            Swal.fire("Error", error.response?.data?.message || "Something went wrong", "error");
        }
        setLoading(false);
    };

    const filteredStats = stats.filter(item =>
        item.Ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.Name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Generate last 12 months dynamically
    const getLast12Months = () => {
        const months = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
        const today = new Date();
        const result = ['Tout le temps'];
        for (let i = 0; i < 12; i++) {
            const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
            result.push(`${months[date.getMonth()]} ${date.getFullYear()}`);
        }
        return result;
    };

    return (
        <AnimationRevealPage>
            <LightAdmin />
            <div className="w-100 p-3">
                <h1 className="text-dark display-4 mb-3">{t('Statistiques_des_ventes')}</h1>

                <div className="d-flex mb-3">
                    <input
                        type="text"
                        className="form-control me-2"
                        placeholder={t('Rechercher_par_ref_ou_nom')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select
                        className="form-select"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        style={{ maxWidth: '200px' }}
                    >
                        {getLast12Months().map(month => (
                            <option key={month} value={month}>{month}</option>
                        ))}
                    </select>
                </div>

                <hr />

                <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <table className="table table-striped table-hover">
                        <thead className="table-primary" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr>
                                <th>{t('Ref')}</th>
                                <th>{t('Nom')}</th>
                                <th>{t('Total_vendu')}</th>
                                <th>{t('Revenue_total')}</th>
                                <th>{t('Pourcentage_Admin_30')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="text-center">Loading...</td>
                                </tr>
                            ) : filteredStats.length > 0 ? (
                                <>
                                    {filteredStats.map(item => (
                                        <tr key={item.ProductID}>
                                            <td>{item.Ref}</td>
                                            <td>{item.Name}</td>
                                            <td>{item.TotalSold}</td>
                                            <td>{item.TotalRevenue.toFixed(2)}</td>
                                            <td>{item.AdminPercentage.toFixed(2)}</td>
                                        </tr>
                                    ))}

                                    {/* Sticky Summary Row */}
                                    <tr
                                        style={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f0f0f0',
                                            position: 'sticky',
                                            bottom: 0,
                                            zIndex: 1
                                        }}
                                    >
                                        <td colSpan={2} className="text-center">{t('Total')}</td>
                                        <td>{filteredStats.reduce((sum, item) => sum + item.TotalSold, 0)}</td>
                                        <td>{filteredStats.reduce((sum, item) => sum + item.TotalRevenue, 0).toFixed(2)}</td>
                                        <td>{filteredStats.reduce((sum, item) => sum + item.AdminPercentage, 0).toFixed(2)}</td>
                                    </tr>
                                </>
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center">No products found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <Footer />
        </AnimationRevealPage>
    );
};

export default ProductSalesAdmin;
